import React, { useState } from "react";
import classNames from "classnames";
import { MenuProps } from "@mui/material";
import { Space } from "@highnote/server/src/core/entities";
import { ReactComponent as EllipsisVerticalSVG } from "App/common/icons-v2/dots-vertical.svg";
import { ButtonV2, ButtonV2Props } from "App/core/ButtonV2";
import { DefaultSpaceActions } from "App/common/SpaceActions";
import { WatcherComponentId } from "App/store/watchers/useWatcherV2";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useGlobalSpaceCached } from "App/store/spaces/useGlobalSpaces";
import { SpaceStub } from "./spaceStub";

/**
 * `DefaultSpaceActions` for a list entry that can be a `SpaceStub`. A stub is
 * the small copy of space fields that a member document carries (see
 * `spaceStub.ts`); it has no role data and no settings fields.
 *
 * Why this component exists. `DefaultSpaceActions` computes
 * `isAllowed(TO_ARCHIVE_SPACE | TO_DELETE_SPACE | TO_MANAGE_SPACE |
 * TO_REMOVE_SELF_FROM_SPACE, { space })` inside a `useMemo` at render time,
 * not on menu open. Hand it a stub and `getFullRoles(stub)` is
 * `{ [createdBy]: [ADMIN] }`, so a manager who is not the creator loses
 * Share, Rename, Settings, New Space, and Move. Every row loses Remove
 * Myself. And a draft space gains a Delete action it does not have today
 * (`TO_DELETE_SPACE` tests `!space.isDraftSpace`, and `undefined` is falsy).
 * `isAllowed` also reads `subscriptionTier`, `sharePasswordEnabled`,
 * `isDraftSpace`, and `privateInboxEnabled` off the entity, and the stub
 * carries none of those. So the real menu renders only after the full space
 * document arrives.
 *
 * A person's interaction, never row count, bounds the fetch: an eager fetch
 * of 100 sidebar roots opens 100 listeners and defeats the reason stubs
 * exist. There is no "menu open" moment inside `SpaceActions` to attach to,
 * so the attach point is the trigger button itself: hover, focus, or a first
 * tap. The cost, stated plainly: on a touch device, the first tap on a
 * stub row's actions button starts the fetch instead of the menu, and the
 * user taps again. That holds only until the document lands, only for stub
 * rows, and only while the new read model is on.
 *
 * With the read flag off, `space` is a real document, and this component is
 * a pass-through to `DefaultSpaceActions`.
 */
export const LazySpaceActions = ({
  space,
  componentId,
  buttonProps,
  hideTriggerButton,
  menuProps,
}: {
  space: SpaceStub;
  componentId: WatcherComponentId;
  buttonProps?: ButtonV2Props;
  hideTriggerButton?: boolean;
  menuProps?: Partial<MenuProps>;
}) => {
  const [interacted, setInteracted] = useState<boolean>(false);
  /*
   * With the read flag on, every list entry is a stub, and a stub carries no
   * roles, so the menu waits for the space document. With the flag off, the
   * entry is the document already.
   */
  const { membersV2Enabled } = useMembershipFlags();
  const { space: fetched } = useGlobalSpaceCached({
    id: space?.id,
    componentId,
    enabled: interacted && membersV2Enabled,
  });
  const resolved = membersV2Enabled ? fetched : (space as Space);

  if (resolved) {
    return (
      <DefaultSpaceActions
        space={resolved}
        buttonProps={buttonProps}
        hideTriggerButton={hideTriggerButton}
        menuProps={menuProps}
      />
    );
  }

  if (hideTriggerButton) return null;

  /*
   * A placeholder trigger, visually identical to the one the real menu
   * renders. It exists so the row keeps its button while the space document
   * still loads.
   */
  return (
    <ButtonV2
      {...buttonProps}
      className={classNames("highnote-actions", buttonProps?.className)}
      data-cypress-id="highnote-actions"
      color={buttonProps?.color || "ghost"}
      size={buttonProps?.size || "sm"}
      aria-label={buttonProps?.["aria-label"] || "More Actions"}
      onMouseEnter={() => setInteracted(true)}
      onFocus={() => setInteracted(true)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setInteracted(true);
      }}
    >
      <EllipsisVerticalSVG />
    </ButtonV2>
  );
};
